import { Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import AnalyticsRouteTracker from "./components/AnalyticsRouteTracker";
import ErrorBoundary from "./components/ErrorBoundary";
import { lazyWithRetry } from "./lib/lazyLoad";

const Shop = lazyWithRetry(() => import("./pages/Shop.tsx"));
const About = lazyWithRetry(() => import("./pages/About.tsx"));
const Login = lazyWithRetry(() => import("./pages/Login.tsx"));
const Signup = lazyWithRetry(() => import("./pages/Signup.tsx"));
const ForgotPassword = lazyWithRetry(() =>
  import("./pages/ForgotPassword.tsx")
);
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword.tsx"));
const OrderSuccess = lazyWithRetry(() => import("./pages/OrderSuccess.tsx"));
const Orders = lazyWithRetry(() => import("./pages/Orders.tsx"));
const AdminOrders = lazyWithRetry(() => import("./pages/AdminOrders.tsx"));
const AdminWaitlist = lazyWithRetry(() => import("./pages/AdminWaitlist.tsx"));
const AdminSignups = lazyWithRetry(() => import("./pages/AdminSignups.tsx"));
const Checkout = lazyWithRetry(() => import("./pages/Checkout.tsx"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe.tsx"));
const Journal = lazyWithRetry(() => import("./pages/Journal.tsx"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound.tsx"));
const ChatWidget = lazyWithRetry(() => import("./components/ChatWidget"));
const CookieConsent = lazyWithRetry(() => import("./components/CookieConsent"));
const AuthProvider = lazyWithRetry(() =>
  import("@/contexts/AuthProvider").then((m) => ({ default: m.AuthProvider }))
);

const queryClient = new QueryClient();

// Defer non-critical UI (chat bubble, cookie banner) off the LCP critical path.
// They mount after the browser is idle, so they no longer chain off the main bundle.
const DeferredUI = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(() => setReady(true), { timeout: 3000 })
      : window.setTimeout(() => setReady(true), 2000);
    return () => {
      if (typeof id === "number") clearTimeout(id);
    };
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <ChatWidget />
      <CookieConsent />
    </Suspense>
  );
};

// Defer Supabase auth client off the initial bundle. Pages that need auth
// (Login, Orders, Checkout, etc.) trigger the import via their own lazy chunks,
// which makes the auth provider mount immediately on navigation. On the landing
// page, auth mounts after idle so the Header gets real session data shortly
// after first paint without blocking it.
const DeferredAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    // Auth-gated routes need the provider quickly; idle on landing is fine.
    const needsAuthNow = /^\/(login|signup|forgot-password|reset-password|orders|admin|checkout|order-success)/.test(
      window.location.pathname
    );
    if (needsAuthNow) {
      setReady(true);
      return;
    }
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(() => setReady(true), { timeout: 3000 })
      : window.setTimeout(() => setReady(true), 2000);
    return () => {
      if (typeof id === "number") clearTimeout(id);
    };
  }, []);
  if (!ready) return <>{children}</>;
  return (
    <Suspense fallback={children}>
      <AuthProvider>{children}</AuthProvider>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ErrorBoundary>
          <DeferredAuthProvider>
            <AnalyticsRouteTracker />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/about" element={<About />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/order-success" element={<OrderSuccess />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/waitlist" element={<AdminWaitlist />} />
                <Route path="/admin/signups" element={<AdminSignups />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <DeferredUI />
          </DeferredAuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
